/**
 * Contact Form Validation
 * Custom form validation to replace browser defaults
 */

class ContactForm {
	constructor(form) {
		this.form = form;

		this.init();
	}

	init() {
		// Validate on submit
		this.form.addEventListener('submit', (e) => this.handleSubmit(e), true);

		const submitButton = this.form.querySelector('[type="submit"]');
		if (submitButton) {
			submitButton.addEventListener('click', (e) => this.handleSubmit(e), true);
		}

		// Use delegated listeners so validation still works if CF7 decorates fields after init.
		this.form.addEventListener('input', (e) => this.handleFieldInput(e));
		this.form.addEventListener('change', (e) => this.handleFieldInput(e));
		this.form.addEventListener('focusout', (e) => this.handleFieldBlur(e));

		// Handle custom dropdown hidden input
		const hiddenDropdown = this.form.querySelector('#inquiry_type');
		if (hiddenDropdown) {
			// Watch for changes via MutationObserver since it's updated by JavaScript
			const observer = new MutationObserver(() => {
				if (hiddenDropdown.value) {
					this.clearError(hiddenDropdown);
				}
			});
			observer.observe(hiddenDropdown, { attributes: true, attributeFilter: ['value'] });
		}
	}

	handleSubmit(e) {
		const isValid = this.validateRequiredFields();

		if (!isValid) {
			e.preventDefault();
			e.stopImmediatePropagation();

			// Focus first error field
			const firstError = this.form.querySelector('.has-error');
			if (firstError) {
				const input = firstError.querySelector('input, textarea, select, button');
				if (input) input.focus();
			}
		}
	}

	validateRequiredFields() {
		let isValid = true;

		// Validate all required fields
		this.getFields().forEach(field => {
			if (!this.validateField(field)) {
				isValid = false;
			}
		});

		if (!isValid) {
			return false;
		}

		return true;
	}

	getFields() {
		return Array.from(this.form.querySelectorAll('input[required], textarea[required], select[required], input[aria-required="true"], textarea[aria-required="true"], select[aria-required="true"], #inquiry_type, #privacy_consent'));
	}

	handleFieldInput(e) {
		if (this.isValidationField(e.target)) {
			this.clearError(e.target);
		}
	}

	handleFieldBlur(e) {
		if (this.isValidationField(e.target)) {
			this.validateField(e.target);
		}
	}

	isValidationField(field) {
		return field instanceof HTMLElement && this.getFields().includes(field);
	}

	validateField(field) {
		const wrapper = field.closest('.contact-form__field, .custom-dropdown, .contact-form__consent');
		if (!wrapper) return true;

		if (this.isRequired(field) && field.type === 'checkbox' && !field.checked) {
			this.showError(wrapper);
			return false;
		}

		// Check if field is required and empty
		if (this.isRequired(field) && !field.value.trim()) {
			this.showError(wrapper);
			return false;
		}

		// Email validation
		if (field.type === 'email' && field.value.trim()) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(field.value)) {
				this.showError(wrapper);
				return false;
			}
		}

		// Phone validation
		if (field.type === 'tel' && field.value.trim() && !this.isValidPhone(field.value)) {
			this.showError(wrapper);
			return false;
		}

		this.clearError(wrapper);
		return true;
	}

	isValidPhone(value) {
		const trimmedValue = value.trim();
		const digits = trimmedValue.replace(/\D/g, '');
		const allowedPhoneCharacters = /^[0-9\s()+./-]+$/;

		return allowedPhoneCharacters.test(trimmedValue) && digits.length >= 7;
	}

	showError(wrapper) {
		wrapper.classList.add('has-error');
		const input = wrapper.querySelector('input, textarea, select');
		if (input) {
			input.setAttribute('aria-invalid', 'true');
		}
	}

	clearError(fieldOrWrapper) {
		const wrapper = fieldOrWrapper.classList.contains('contact-form__field') ||
			fieldOrWrapper.classList.contains('custom-dropdown') ||
			fieldOrWrapper.classList.contains('contact-form__consent')
			? fieldOrWrapper
			: fieldOrWrapper.closest('.contact-form__field, .custom-dropdown, .contact-form__consent');

		if (wrapper) {
			wrapper.classList.remove('has-error');
			const input = wrapper.querySelector('input, textarea, select');
			if (input) {
				input.removeAttribute('aria-invalid');
			}
		}
	}

	isRequired(field) {
		return field.hasAttribute('required') ||
			field.getAttribute('aria-required') === 'true' ||
			field.id === 'inquiry_type' ||
			field.id === 'privacy_consent';
	}
}

function initializeContactForms() {
	const contactForm = document.getElementById('contact-form');
	if (contactForm && !contactForm.dataset.contactValidationInitialized) {
		contactForm.dataset.contactValidationInitialized = 'true';
		new ContactForm(contactForm);
	}
}

function renderContactConfirmation(event) {
	const form = event.target;
	if (!form || form.id !== 'contact-form') return;

	form.innerHTML = `
		<div class="contact-form__confirmation">
			<h2 class="contact-form__confirmation-heading">Thank you</h2>
			<p class="contact-form__confirmation-message">
				Our team will review your inquiry and get back to you shortly.
			</p>
		</div>
	`;
	form.classList.add('contact-form--submitted');
}

function markContactValidationErrors(event) {
	const form = event.target;
	if (!form || form.id !== 'contact-form') return;

	form.querySelectorAll('.has-error').forEach(wrapper => wrapper.classList.remove('has-error'));
	form.querySelectorAll('.wpcf7-not-valid').forEach(field => {
		const wrapper = field.closest('.contact-form__field, .custom-dropdown, .contact-form__consent');
		if (wrapper) {
			wrapper.classList.add('has-error');
		}
	});
}

function bootContactForms() {
	initializeContactForms();
	document.addEventListener('wpcf7mailsent', renderContactConfirmation);
	document.addEventListener('wpcf7invalid', markContactValidationErrors);
}

// Initialize contact form validation
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', bootContactForms);
} else {
	bootContactForms();
}

export default ContactForm;
